import { ContentHeader } from 'components/ui/layout/ContentHeader';
import { useTranslation } from 'react-i18next';
import { DarwinListView } from 'components/darwin-ui';

import { PermissionActions, PermissionModules } from 'types/rolesTypes';

import useTableControls from 'hooks/useTableControls';
import { generatePath, useNavigate } from 'react-router-dom';

import icons from 'components/darwin-ui/Icon/Icons';
import { ROUTES } from 'routes/routes';

import { useGetUserList } from 'api/services/userService';
import { useEffect, useState, useCallback } from 'react';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { UserRecord } from 'types/userTypes';
import { getUsersColumns } from './columns';
import { DeleteUserModal } from 'components/ui/users/DeleteUserModal';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useUI } from 'contexts/UiContext';

import { ActivateDeactivateUser } from 'components/ui/users/ActivateDeactivateModal';

export type ColumnAction = {
  key: string;
  name?: keyof typeof icons;
  label?: string;
  onClick?: (user: UserRecord) => void;
};

export const Users = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectedCompany } = useUI();
  const [userToDelete, setUserToDelete] = useState<UserRecord>();
  const [userToActivateDeactivate, setUserToActivateDeactivate] =
    useState<UserRecord>();

  const checkPermission = usePermissionChecker();

  const viewUsersPermission = checkPermission(
    PermissionModules.Users,
    PermissionActions.Read
  );

  const deleteUserPermission = checkPermission(
    PermissionModules.Users,
    PermissionActions.Delete
  );
  const activateDeactivateUserPermission = checkPermission(
    PermissionModules.Users,
    PermissionActions.Update
  );

  const editUserPermission = checkPermission(
    PermissionModules.Users,
    PermissionActions.Update
  );

  const {
    pageSize,
    currentPage,
    onPageSizeChange,
    setCurrentPage,
    searchQuery,
    setSearchQuery,
    sortOrder,
    onTableChange,
  } = useTableControls({
    defaultSortBy: 'user.firstName',
  });

  const { openNotification } = useFloatNotification();

  const {
    data: usersData,
    isLoading: isLoadingUsers,
    isFetching: isFetchingUsers,
    isPending: isPendingUsers,
    error: usersError,
  } = useGetUserList({
    page: currentPage,
    limit: pageSize,
    query: searchQuery,
    sortOrder,
  });

  useEffect(() => {
    if (usersError?.message) {
      openNotification(
        'Error',
        t('users-page.users-list.loading-error'),
        'error'
      );
    }
  }, [usersError?.message, openNotification, t]);

  const isLoading =
    isLoadingUsers ||
    isFetchingUsers ||
    isPendingUsers ||
    viewUsersPermission === null;

  const tableData = usersData?.data ?? [];

  const availableActions: Array<{
    permission: boolean | null;
    action: ColumnAction;
  }> = [
    {
      permission: editUserPermission,
      action: {
        key: 'edit',
        name: 'edit',
        label: t('list-view.action-items.edit'),
        onClick: user => {
          navigate(
            generatePath(ROUTES.USERS.EDIT, {
              id: user.id,
            })
          );
        },
      },
    },
    {
      permission: viewUsersPermission,
      action: {
        key: 'view',
        name: 'view',
        label: t('list-view.action-items.view'),
        onClick: user => {
          navigate(
            generatePath(ROUTES.USERS.VIEW, {
              id: user.id,
            })
          );
        },
      },
    },
    {
      permission: deleteUserPermission,
      action: {
        key: 'delete',
        name: 'delete',
        label: t('list-view.action-items.delete'),
        onClick: setUserToDelete,
      },
    },
    {
      permission: activateDeactivateUserPermission,
      action: {
        key: 'activate-deactivate',
        onClick: setUserToActivateDeactivate,
      },
    },
  ];

  const actions: ColumnAction[] = availableActions
    .filter(item => Boolean(item.permission))
    .map(item => item.action);

  const columns = getUsersColumns(t, actions, searchQuery);

  const handleUserCancel = useCallback(() => {
    setUserToActivateDeactivate(undefined);
  }, [setUserToActivateDeactivate]);

  return (
    <>
      <ContentHeader
        title={t('users-page.users-list.title')}
        onSave={() => {
          navigate(ROUTES.USERS.CREATE);
        }}
        onSaveTitle={t('users-page.users-list.create-new-user')}
      />

      <DarwinListView<UserRecord>
        data={tableData}
        columns={columns}
        isLoading={isLoading}
        header={{
          setSearchQuery,
          searchPlaceholder: t('users-page.users-list.search-placeholder'),
        }}
        table={{ onChange: onTableChange }}
        behavior={{
          onRowClick: user =>
            navigate(
              generatePath(ROUTES.USERS.EDIT, {
                id: user.id,
              })
            ),
          isRowInactive: user => !user.active,
        }}
        pagination={{
          total: usersData?.metadata?.total,
          pageSize,
          currentPage,
          onPageChange: setCurrentPage,
          onPageSizeChange,
        }}
      />

      <DeleteUserModal
        open={!!userToDelete}
        onCancel={() => setUserToDelete(undefined)}
        companyId={selectedCompany || ''}
        userCompanyRoleId={userToDelete?.id.toString()}
      />

      <ActivateDeactivateUser
        open={!!userToActivateDeactivate}
        onCancel={handleUserCancel}
        selectedCompany={selectedCompany || ''}
        user={userToActivateDeactivate}
      />
    </>
  );
};
